import { Inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, mergeMap, tap, concatAll } from 'rxjs/operators';
import { PermitsV2MetaDataItemNames, Permits2PermitEntity, PermitsV2MetaDataService, NoteTypeAllItems, PermitStatusAllItems, 
         CodeValueInspectionResults, CodeValueInspectionState, Permits2RoInspectionsEntity, ChangeRequestEntity } 
         from '../meta-data/permitsv2-meta-data.service';
import { DwMetaDataRoutingService, DwActionResult, DwMenuActionType, DwAction, DwActionType, DwMdFormActionResult, DwMdFormActionHandler,
         DwMdFormActionState, DwQueryHelper, DwModalConfig, DwModalService, DwModalSize, DwOrmDataService, DwOrmDataServiceToken, DwFormEventService,
         DwConfirmDialogConfig, DwSecurityUserService, DwMdFormInitActionState, DwMdFormInitActionHandler, DwModalButtonStandardConfigs, DwModalResult,
         DwMessagingService, DwMessageSeverity } 
         from '@devwareapps/devware-cap';
import { WachterUserExt } from 'src/app/shared/models/wachter-user.model';
import { ChangeRequestModalComponent } from '../components/change-request-modal/change-request-modal.component';
import { NotesRepositoryService } from 'src/app/shared/notes/services/notes-repository-service';
import { NotesMetaDataItemNames, NotesMetaDataService, NotesNoteParentEntity, NotesNoteEntity } from 'src/app/shared/notes/meta-data/notes-meta-data.service';
import * as moment_ from 'moment';
const moment = moment_;

@DwAction({
  keyName: 'Permits_V2_Change_Request',
  display: 'Permits V2 - Change Request Management',
  actionType: DwActionType.formAction,
  isGlobal: false,
  parentItemName: [ PermitsV2MetaDataItemNames.Permits2Permit ],
  defaultButtonConfig: {
    buttonClass: 'btn-sm dw-yellow-bg mr-1 mt-1',
    iconClass: 'fad fa-exchange',
    buttonText: 'Request Change'
  },
})
@Injectable()
export class PermitV2ChangeRequestActionService implements DwMdFormActionHandler, DwMdFormInitActionHandler {
  constructor(
    private dwSecurityUserService: DwSecurityUserService,
    private metaDataRoutingService: DwMetaDataRoutingService,
    private formEventService: DwFormEventService,
    private notesRepository: NotesRepositoryService,
    protected messageService: DwMessagingService,
        private dwModalService: DwModalService,
    protected securityService: DwSecurityUserService,
    @Inject(DwOrmDataServiceToken) private dataService: DwOrmDataService
  ) { }

  formInitActionState: DwMdFormInitActionState;
  completeMsg: string = 'Complete Change Request';
  
  lastModified?: string;
  contextKey: string;
    /**
     * Initialize the action to customize any config items
     * @param formInitActionState form state
     */
    initAction(formInitActionState: DwMdFormInitActionState) {
        this.formInitActionState = formInitActionState;
        this.contextKey = this.formInitActionState.config.variableContextDataKey;
        this.formInitActionState.formApi.getFormDataChanges(100).subscribe(p => {
            if (p.ChangeRequestId == null) return;

            if (this.isNewer(p.ModifiedByDateTime))
                this.dataService.loadEntity(PermitsV2MetaDataItemNames.ChangeRequest, p.ChangeRequestId)
                    .subscribe(cr => {
                        const changeRequest : ChangeRequestEntity = cr;
                        if (changeRequest.InProgress)
                            this.formInitActionState.formApi.patchContextData(this.contextKey, { ChangeRequestInProgress: true });
                    });
            this.lastModified = p.ModifiedByDateTime;
        });
    }

    private sendInfoMsg(){
        this.messageService.showMessage({messageHeading: '', messageBody: 'Your Change Request has been submitted', severity: DwMessageSeverity.info });
    }
    isNewer(modified: string) : boolean {
        if (this.lastModified == undefined || modified == undefined || this.lastModified == modified)
            return false;
        return moment(modified).isAfter(this.lastModified);
    }
    handleComplete(permit: Permits2PermitEntity, userId: number, formActionState: DwMdFormActionState): Observable<DwActionResult>{
        const confirmDialogConfig: DwConfirmDialogConfig = {
            message: "Are you certain you want to mark this 'Change Request' as Complete?",
            title: 'Mark Complete?',
            showConfirmDialog: true,
        };
        return this.dwModalService.showConfirmDialog(confirmDialogConfig)
                   .pipe(map(result => 
                     {
                        if (result){
                            this.dataService.loadEntity(PermitsV2MetaDataItemNames.ChangeRequest, permit.ChangeRequestId)
                                .pipe(map(i =>
                                    {
                                        const changeRequest : ChangeRequestEntity = i;
                                        changeRequest.CompletedOnDateTime = this.getCurrentDateTime();
                                        changeRequest.CompletedByUserId = userId;
                                        changeRequest.InProgress = false;
                                        this.saveChangeRequest(changeRequest).subscribe(id => {
                                            this.formInitActionState.formApi.patchContextData(this.contextKey, { ChangeRequestCompleted: true });
                                        });
                                    })).subscribe();
                        }
                        return { success: result };
                     }));
    }  
    showChangeRequestModal(userId: number, note: NotesNoteEntity, permit: Permits2PermitEntity): Observable<DwModalResult>{
        let modalConfig = new DwModalConfig();

        modalConfig.buttons = DwModalButtonStandardConfigs.instance.SaveAndCancel;
        modalConfig.allowFullScreen = false;

        modalConfig.buttons[0].buttonText = 'Submit';
        modalConfig.buttons[0].buttonClass = 'btn btn-success';
        modalConfig.buttons[0].iconClass = 'fa fa-save';
        modalConfig.buttons[0].hidden = true;

        modalConfig.buttons[1].buttonText = 'Cancel';
        modalConfig.buttons[1].buttonClass = 'btn btn-danger';
        modalConfig.buttons[1].iconClass = 'fa fa-times-circle';

        modalConfig.component = ChangeRequestModalComponent;
        modalConfig.modalSize = DwModalSize.custom;
        modalConfig.customModalWidth = "460px"
        modalConfig.customModalHeight = "260px";

        modalConfig.title = 'Add Comments:';    

        return this.dwModalService.showModal(modalConfig) 
                    .pipe(map(modalResult => {
                                if (!modalResult.cancelled)
                                {
                                    modalResult.data = {
                                        CreatedByUserId: userId,
                                        CreatedByDateTime: this.getCurrentDateTime(),
                                        NoteId: note.NoteId,
                                        InProgress: false,
                                        InitialPermitStatusId: permit.PermitStatusId,
                                        CompletedByUserId: null,
                                        CompletedOnDateTime: null,
                                        Comment: modalResult.data
                                    };
                                    this.sendInfoMsg();
                                }
                                return modalResult;
          }));
      }
    
    handleRequest(permit: Permits2PermitEntity, userId: number, formActionState: DwMdFormActionState) : Observable<DwActionResult>{
        const mode = DwMenuActionType.add;
        const type = PermitsV2MetaDataItemNames.ChangeRequest;

        return this.addNote(permit.NoteParentId)
                   .pipe(map(note => {
                            return this.showChangeRequestModal(userId, note, permit)
                                .pipe(map(modalResult => {
                                    if (modalResult.cancelled)
                                        this.deleteNote(note.NoteId);
                                    else {
                                        note.Note = '<span><i class="fad fa-exchange-alt"></i></span>&nbsp;' + modalResult.data.Comment;
                                        this.updateNote(note);

                                        const cr : ChangeRequestEntity = modalResult.data;
                                        this.saveChangeRequest(cr).subscribe(id => {
                                            const update = { 
                                                ChangeRequestId: id,
                                                AssignedToUserId: null,
                                                FollowUpDate: null,
                                                FollowUpDescription : null
                                            };
                                            formActionState.formApi.patchFormData(update);

                                            const permit = formActionState.formApi.getFormData();
                                            permit.ChangeRequestId = id;
                                            permit.FollowUpDate = null;
                                            permit.FollowUpDescription = null;
                                            permit.AssignedToUserId = null;
                                            return this.dataService.saveEntity(permit).subscribe(i => { });
                                        });
                                    }
                                    const result : DwActionResult = { 
                                        success: !modalResult.cancelled
                                    };
                                    return result;
                                }));
                    })).pipe(concatAll());
    } 
    handleAction(formActionState: DwMdFormActionState): Observable<DwActionResult> {
        const isComplete = formActionState.actionPreference.actionDisplay == this.completeMsg;
        const permitEntity: Permits2PermitEntity = formActionState.formApi.getFormData();
        const userExt = new WachterUserExt(this.securityService.securityContext.ApplicationUser);
        const userId: number = userExt.trustUserId;
        return isComplete ? this.handleComplete(permitEntity, userId, formActionState) :
                            this.handleRequest(permitEntity, userId, formActionState);
    }
    private getCurrentDateTime(): string {
        let dateTimeStr =  moment().utc().format("MM/DD/YYYY hh:mm A")
        return dateTimeStr;
    }
    addNote(noteParentId: number) : Observable<NotesNoteEntity> { return this.notesRepository.createNote("", noteParentId, NoteTypeAllItems.ChangeRequest); }
    updateNote(note: NotesNoteEntity) { 
        this.notesRepository.updateNote(note)
            .subscribe(x => {
                this.notesRepository.publishNoteRefreshEvent(note.NoteTypeId);  
            });
    }
    deleteNote(noteId: number) { 
        this.notesRepository.deleteNote(noteId)
          .subscribe(x => { });
    }
        saveChangeRequest(cr: ChangeRequestEntity): Observable<number> {
            cr._keyName = PermitsV2MetaDataItemNames.ChangeRequest + "Id";
            cr._itemName = PermitsV2MetaDataItemNames.ChangeRequest;
            return this.dataService.saveEntity(cr).pipe(map((savedCr: ChangeRequestEntity) => {
                return savedCr.ChangeRequestId;
            }));
        }
}
